import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { Filter, Loader2, History } from 'lucide-react';
import { resolutionApi, type PersistedCluster } from '@/services/api';
import { useKnowledgeStore } from '@/stores/knowledge';

function timeAgo(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

export function ClusterHistoryTab() {
  const [items, setItems] = useState<PersistedCluster[]>([]);
  const [loading, setLoading] = useState(false);
  const [entityType, setEntityType] = useState<string>('');
  const [clientUid, setClientUid] = useState<string>('');
  const { entityTypes, fetchEntityTypes } = useKnowledgeStore();

  const load = () => {
    setLoading(true);
    resolutionApi
      .listClusterHistory({
        entity_type: entityType || undefined,
        client_uid: clientUid || undefined,
        limit: 200,
      })
      .then((res) => setItems(res.clusters))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchEntityTypes();
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, clientUid]);

  const avgSize =
    items.length > 0
      ? items.reduce((s, c) => s + c.cluster_size, 0) / items.length
      : 0;
  const maxSize = items.reduce((m, c) => Math.max(m, c.cluster_size), 0);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3 flex-wrap">
        <Filter size={12} className="text-slate-400" />
        <select
          value={entityType}
          onChange={(e) => setEntityType(e.target.value)}
          className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5"
        >
          <option value="">All entity types</option>
          {entityTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Client UID (optional)"
          value={clientUid}
          onChange={(e) => setClientUid(e.target.value)}
          className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 w-48"
        />
        {loading && <Loader2 size={12} className="animate-spin text-slate-400 ml-auto" />}
      </div>

      {items.length === 0 && !loading ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <History size={24} className="text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">
            No persisted clusters yet for this filter.
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Clusters are persisted to <code>resolution_clusters</code> when
            ``POST /clusters/find`` runs (Phase 4).
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between text-xs">
            <span className="font-semibold text-navy-900">
              {items.length} clusters · avg size {avgSize.toFixed(1)} · max {maxSize}
            </span>
          </div>
          <div className="max-h-[60vh] overflow-auto divide-y divide-slate-100">
            {items.map((c) => (
              <Link
                key={c.id}
                to={`/knowledge/resolution`}
                onClick={(e) => e.preventDefault()}
                className="block"
              >
                <ClusterRow cluster={c} />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ClusterRow({ cluster }: { cluster: PersistedCluster }) {
  return (
    <div className="px-4 py-2.5 flex items-center gap-3 text-xs hover:bg-slate-50 transition-colors">
      <span className="text-sm font-medium text-slate-800 truncate flex-1">
        {cluster.centroid_name ?? `cluster ${cluster.id.slice(0, 8)}`}
      </span>
      <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium text-[10px]">
        {cluster.entity_type}
      </span>
      {cluster.client_uid && (
        <span className="text-[10px] text-slate-500 truncate max-w-[8rem]">
          {cluster.client_uid}
        </span>
      )}
      <span className="text-[10px] text-slate-500">
        size {cluster.cluster_size}
      </span>
      {cluster.similarity_mean != null && (
        <span className="text-[10px] text-slate-500">
          sim {(cluster.similarity_mean * 100).toFixed(0)}%
        </span>
      )}
      <span
        className={clsx(
          'text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border',
          cluster.resolved
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-amber-50 text-amber-700 border-amber-200',
        )}
      >
        {cluster.resolved ? 'resolved' : 'pending'}
      </span>
      <span className="text-[10px] text-slate-400 ml-auto">
        {timeAgo(cluster.created_at)}
      </span>
    </div>
  );
}
